import React, { useRef } from "react";
import styled from "@emotion/styled";
import { Grid, Typography } from "@mui/material";
import wallet from '../../../../assets/images/wallet.png';
import mixer from '../../../../assets/images/mixer.png';
import bridge from '../../../../assets/images/bridge.png';
import aiagent from '../../../../assets/images/aiagent.png';

const Wrapper = styled.div`
  padding: 120px 32px;

  @media (max-width: 768px) {
    padding: 96px 22px 90px;
  }

  @media (max-width: 480px) {
    padding: 220px 22px 78px;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
`;

const Heading = styled(Typography)`
  font-size: 56px;
  font-family: "primary1" !important;
  line-height: 1;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    font-size: 42px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 42px;
    margin-bottom: 24px;
  }
`;

const Paragraph = styled(Typography)`
  font-size: 33px;
  font-weight: 400;
  line-height: 1.08;
  max-width: 1100px;
  margin-top: 0px;
  color: #000;
  text-align: justify;
  text-align-last: left;

  @media (max-width: 768px) {
    font-size: 20px;
    max-width: 100%;
    line-height: 1.1;
    text-align: justify;
    text-align-last: left;
  }
`;

const CardsGrid = styled(Grid)`
  margin-top: 34px;

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const AboutCard = styled.div`
  height: 100%;
  min-height: 230px;
  border-radius: 8px;
  padding: 30px 34px;
  background: #f8fafb65;
  box-shadow: 0 25px 48.2px 0 #c2dfece6 inset;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  will-change: transform;
  transition: box-shadow 0.4s ease;
  cursor: default;

  @media (max-width: 768px) {
    min-height: 210px;
    padding: 24px;
  }

  @media (max-width: 420px) {
    min-height: 190px;
    padding: 20px;
  }
`;

const CardIcon = styled.img`
  width: 78px;
  height: 78px;
  object-fit: contain;
  flex: 0 0 auto;

  @media (max-width: 420px) {
    width: 66px;
    height: 66px;
  }
`;

const CardIconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 420px) {
    margin-bottom: 12px;
  }
`;

const ComingSoonBadge = styled.span`
  color: #000;
  font-size: 13px;
  line-height: 1;
  font-family: "primary1" !important;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 999px;
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.74);
  white-space: nowrap;
`;

const CardTitle = styled(Typography)`
  color: #000;
  font-size: 23px;
  line-height: 1.1;
  font-family: "primary1" !important;
  margin-bottom: 12px;
  display: block;

  @media (max-width: 420px) {
    font-size: 20px;
  }
`;

const CardText = styled(Typography)`
  color: #000;
  font-size: 18px;
  line-height: 1.25;
  max-width: 450px;
  margin-top: 5px;
  display: block;

  @media (max-width: 420px) {
    font-size: 13px;
  }
`;

const aboutCards = [
  {
    icon: wallet,
    title: "Ephemeral Wallet",
    text: "Disposable wallets created on-demand. No address reuse, no identity buildup.",
  },
  {
    icon: mixer,
    title: "Mixer",
    text: "Breaks wallet traceability with disposable routing paths. Prevents tracking and on-chain identity linking.",
  },
  {
    icon: bridge,
    title: "Private Swap",
    text: "Swap tokens without exposing your main wallet. Trades route through ephemeral wallets so swaps can't be linked back to your identity.",
  },
  {
    icon: aiagent,
    title: "Zephyr AI Agent",
    text: "Autonomously executes Web3 actions across dApps and protocols. Optimizes decisions while preserving privacy through isolated sessions.",
  },
];

const aboutText =
  "Designed specifically for decentralized applications, Zephyr gives users greater control over their privacy while browsing, connecting wallets, and interacting onchain. Starting with Auto Burn Sessions, Zephyr is building the next generation of private Web3 infrastructure on Solana Chain.";

const TiltCard = ({ card, index }) => {
  const cardRef = useRef(null);
  const frameRef = useRef(null);
  const motionRef = useRef({ rotateX: 0, rotateY: 0, targetX: 0, targetY: 0 });

  const lerp = (a, b, t) => a + (b - a) * t;

  const animate = () => {
    const m = motionRef.current;
    m.rotateX = lerp(m.rotateX, m.targetX, 0.15);
    m.rotateY = lerp(m.rotateY, m.targetY, 0.15);

    if (cardRef.current) {
      cardRef.current.style.transform = `perspective(600px) rotateX(${m.rotateX}deg) rotateY(${m.rotateY}deg)`;
    }

    if (
      Math.abs(m.rotateX - m.targetX) > 0.01 ||
      Math.abs(m.rotateY - m.targetY) > 0.01
    ) {
      frameRef.current = requestAnimationFrame(animate);
    } else {
      m.rotateX = m.targetX;
      m.rotateY = m.targetY;
      if (cardRef.current) {
        cardRef.current.style.transform = `perspective(600px) rotateX(${m.rotateX}deg) rotateY(${m.rotateY}deg)`;
      }
      frameRef.current = null;
    }
  };

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    motionRef.current.targetY = x * 30;
    motionRef.current.targetX = y * 22;

    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(animate);
    }
  };

  const handleMouseLeave = () => {
    motionRef.current.targetX = 0;
    motionRef.current.targetY = 0;

    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(animate);
    }
  };

  return (
    <AboutCard
      ref={cardRef}
      data-aos="fade-up"
      data-aos-delay={index * 120}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <CardIconRow>
        <CardIcon src={card.icon} alt="" aria-hidden="true" />
        {card.title !== "Ephemeral Wallet" && card.title !== "Mixer" && (
          <ComingSoonBadge>Coming Soon</ComingSoonBadge>
        )}
      </CardIconRow>
      <CardTitle>{card.title}</CardTitle>
      <CardText>{card.text}</CardText>
    </AboutCard>
  );
};

const About = () => {
  return (
    <Wrapper data-aos="fade-up" data-aos-duration="900" id="about">
      <Inner>
        <Grid container alignItems="center" mb={4}>
          <Grid item xs={12}>
            <Heading>About Zephyr</Heading>
            <Paragraph>{aboutText}</Paragraph>
          </Grid>
        </Grid>
        <CardsGrid container spacing={{ xs: 2, sm: 3 }}>
          {aboutCards.map((card, index) => (
            <Grid item xs={12} md={6} key={card.title}>
              <TiltCard card={card} index={index} />
            </Grid>
          ))}
        </CardsGrid>
      </Inner>
    </Wrapper>
  );
};

export default About;
